import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import PageHeader from '@/components/PageHeader';
import AdvanceSharePackForm from './advance-share-pack-form';
import AdvanceSharePackItemList from './advance-share-pack-item-list';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { AdvanceSharePack } from '@/models/advance-share-pack.model';
import { getAdvanceSharePack } from '@/services/advance-share-pack.service';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect}>
      <Nav.Item eventKey="pack-form">Pack Detail</Nav.Item>
      <Nav.Item eventKey="pack-items" disabled={newMode}>Items</Nav.Item>
    </Nav>
  );
};

const Page = () => {

  const requestedTab = useLocation()?.state?.tab;

  const [activeTab, setActiveTab] = useState('pack-form');
  const [formObject, setFormObject] = useState<AdvanceSharePack | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;

  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);

  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<AdvanceSharePack> = await getAdvanceSharePack(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching Pack: ${errorModel?.errorMessage}`);
        nav('/advance-shares/packs');
      }
      finally {
        setLoading(false);
      }
    }
  };

  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit && formObject ? `Edit Pack: ${formObject.title}` : 'New Pack'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item >Advance Shares</Breadcrumb.Item>
            <Breadcrumb.Item href="/advance-shares/packs">Packs</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" active={activeTab} onSelect={key => setActiveTab(key)} newMode={pageState == PageState.New} />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'pack-form' && <AdvanceSharePackForm formObject={formObject} pageState={pageState} />}
          {activeTab == 'pack-items' && id && <AdvanceSharePackItemList packId={id} />}
        </>
      }
    </Panel>
  );
};

export default Page;
